import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { updateError } from './user';
import { callApi } from '../../utils/apiCall';

const initialState = {
    isLoading: false,
    formsLoading: false,
    error: null,
    forms: { results: [], count: 0 },
    currentForm: null,
    submissions: { results: [], count: 0 },
    currentSubmission: null,
    consentRequests: { results: [], count: 0 },
    leadConsentRequests: { results: [], count: 0 },
    leadConsentLoading: false,
    // Submissions scoped to a single lead (for LeadFormsTab)
    leadSubmissions: { results: [], count: 0 },
    leadSubmissionsLoading: false,
    formTypes: [],
    formTypesLoading: false,
    // Auto-send trigger rules (stored as practice choices)
    formTriggers: [],
    formTriggersLoading: false,
    // Practice-level form branding config (stored as a practice choice)
    formBranding: null,
    formBrandingLoading: false,
    // Treatment plan templates
    treatmentPlanTemplates: { results: [], count: 0 },
    treatmentPlanTemplatesLoading: false,
    // Per-lead treatment plans (for LeadFormsTab)
    leadTreatmentPlans: { results: [], count: 0 },
    leadTreatmentPlansLoading: false,
};

const slice = createSlice({
    name: 'consentV2',
    initialState,
    reducers: {
        startLoading(state) {
            state.isLoading = true;
        },
        stopLoading(state) {
            state.isLoading = false;
        },
        hasError(state, action) {
            state.isLoading = false;
            state.formsLoading = false;
            state.error = action.payload;
        },
        startFormsLoading(state) {
            state.formsLoading = true;
        },
        setForms(state, action) {
            state.isLoading = false;
            state.formsLoading = false;
            state.forms = action.payload;
        },
        setCurrentForm(state, action) {
            state.isLoading = false;
            state.currentForm = action.payload;
        },
        setSubmissions(state, action) {
            state.isLoading = false;
            state.submissions = action.payload;
        },
        setCurrentSubmission(state, action) {
            state.isLoading = false;
            state.currentSubmission = action.payload;
        },
        resetCurrentForm(state) {
            state.currentForm = null;
        },
        setConsentRequests(state, action) {
            state.isLoading = false;
            state.consentRequests = action.payload;
        },
        setLeadConsentRequests(state, action) {
            state.leadConsentLoading = false;
            state.leadConsentRequests = action.payload;
        },
        startLeadConsentLoading(state) {
            state.leadConsentLoading = true;
        },
        clearLeadConsentRequests(state) {
            state.leadConsentRequests = { results: [], count: 0 };
            state.leadConsentLoading = false;
        },
        setLeadSubmissions(state, action) {
            state.leadSubmissionsLoading = false;
            state.leadSubmissions = action.payload;
        },
        startLeadSubmissionsLoading(state) {
            state.leadSubmissionsLoading = true;
        },
        clearLeadSubmissions(state) {
            state.leadSubmissions = { results: [], count: 0 };
            state.leadSubmissionsLoading = false;
        },
        startFormTypesLoading(state) {
            state.formTypesLoading = true;
        },
        setFormTypes(state, action) {
            state.formTypes = action.payload;
            state.formTypesLoading = false;
        },
        stopFormTypesLoading(state) {
            state.formTypesLoading = false;
        },
        setFormTriggers(state, action) {
            state.formTriggers = action.payload;
            state.formTriggersLoading = false;
        },
        startFormTriggersLoading(state) {
            state.formTriggersLoading = true;
        },
        setFormBranding(state, action) {
            state.formBranding = action.payload;
            state.formBrandingLoading = false;
        },
        startFormBrandingLoading(state) {
            state.formBrandingLoading = true;
        },
        setTreatmentPlanTemplates(state, action) {
            state.treatmentPlanTemplates = action.payload;
            state.treatmentPlanTemplatesLoading = false;
        },
        startTreatmentPlanTemplatesLoading(state) {
            state.treatmentPlanTemplatesLoading = true;
        },
        stopTreatmentPlanTemplatesLoading(state) {
            state.treatmentPlanTemplatesLoading = false;
        },
        setLeadTreatmentPlans(state, action) {
            state.leadTreatmentPlans = action.payload;
            state.leadTreatmentPlansLoading = false;
        },
        startLeadTreatmentPlansLoading(state) {
            state.leadTreatmentPlansLoading = true;
        },
        clearLeadTreatmentPlans(state) {
            state.leadTreatmentPlans = { results: [], count: 0 };
            state.leadTreatmentPlansLoading = false;
        },
    },
});

export default slice.reducer;

export const {
    resetCurrentForm,
    clearLeadConsentRequests,
    clearLeadSubmissions,
    clearLeadTreatmentPlans,
    setFormTypes,
} = slice.actions;

export function getConsentForms(practiceId, params = {}) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            dispatch(slice.actions.startFormsLoading());
            const searchParams = new URLSearchParams();
            searchParams.set('ordering', '-created_at');
            if (params.formType) searchParams.set('form_type', params.formType);
            if (params.offset !== undefined) searchParams.set('offset', params.offset);
            if (params.limit !== undefined) searchParams.set('limit', params.limit);
            if (params.search) searchParams.set('search', params.search);
            // Optional ``is_template`` filter — Send Forms wizard passes
            // ``isTemplate=false`` because templates can't be dispatched
            // to a patient. The BE ``PracticeFormViewSet`` exposes this
            // through filterset_fields["is_template"]["exact"].
            if (params.isTemplate !== undefined) {
                searchParams.set('is_template', params.isTemplate ? 'true' : 'false');
            }
            // Perf opt-in: callers that don't need the full nested
            // sections/treatments/conditional_logics graph can pass a
            // ``params.fields`` projection — the BE
            // ``PracticeFormViewSet`` parses ``?fields=`` and skips
            // prefetches for relations that aren't requested
            // (apps/practice/views.py — 2026-04-26 perf pass). Default is
            // unset so existing callers (ConsentFormsList,
            // ConsentV2LinkDialog) which depend on description /
            // treatments / created_by keep working unchanged.
            if (params.fields) {
                searchParams.set('fields', params.fields);
            }
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/?${searchParams.toString()}`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setForms(response.data));
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function getConsentFormDetail(practiceId, formId) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/${formId}/`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setCurrentForm(response.data));
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function createConsentForm(practiceId, data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            // Backend now supports top-level enable_memory, form_mode, step_groups, version fields.
            const payload = { ...data, form_type: data.form_type || 'CONSENT' };
            if (data.enable_memory !== undefined) payload.enable_memory = data.enable_memory;
            if (data.form_mode) payload.form_mode = data.form_mode;
            if (data.step_groups) payload.step_groups = data.step_groups;
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/`,
                'post',
                payload
            );
            if (response.status === 201) {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: true, data: response.data });
            } else {
                dispatch(slice.actions.hasError(response.data));
                // When a callback is supplied (the form builder always does),
                // it surfaces a structured, user-readable error itself. Avoid
                // also dispatching ``updateError`` with ``JSON.stringify(response.data)``
                // — that blob (a) shows a second confusing snackbar and
                // (b) trips the global ``errorHandler``'s archived-lead heuristic
                // when nested DRF errors contain "Invalid pk" / "object does not exist".
                if (callback) callback({ success: false, data: response.data });
                else dispatch(updateError({ success: false, message: 'Failed to save form' }));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            if (callback) callback({ success: false });
            else dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function updateConsentForm(practiceId, formId, data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            // Backend now supports top-level enable_memory, form_mode, step_groups, version fields.
            const payload = { ...data, form_type: data.form_type || 'CONSENT' };
            if (data.enable_memory !== undefined) payload.enable_memory = data.enable_memory;
            if (data.form_mode) payload.form_mode = data.form_mode;
            if (data.step_groups) payload.step_groups = data.step_groups;
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/${formId}/`,
                'patch',
                payload
            );
            if (response.status === 200) {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: true, data: response.data });
            } else {
                dispatch(slice.actions.hasError(response.data));
                // See note in ``createConsentForm`` — defer to the caller's
                // callback for the user-visible message instead of
                // ``JSON.stringify``-ing the nested error blob into the
                // global snackbar.
                if (callback) callback({ success: false, data: response.data });
                else dispatch(updateError({ success: false, message: 'Failed to update form' }));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            if (callback) callback({ success: false });
            else dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function deleteConsentForm(practiceId, formId, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/${formId}/`,
                'delete'
            );
            if (response.status === 204) {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: true });
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
                if (callback) callback({ success: false });
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            if (callback) callback({ success: false });
        }
    };
}

export function getConsentSubmissions(practiceId, formId, params = {}) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const searchParams = new URLSearchParams();
            searchParams.set('ordering', '-created_at');
            if (params.offset !== undefined) searchParams.set('offset', params.offset);
            if (params.limit !== undefined) searchParams.set('limit', params.limit);
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/${formId}/submissions/?${searchParams.toString()}`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setSubmissions(response.data));
            } else {
                dispatch(slice.actions.hasError(response.data));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
        }
    };
}

export function getConsentSubmissionDetail(practiceId, formId, submissionId) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/${formId}/submissions/${submissionId}/`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setCurrentSubmission(response.data));
            } else {
                dispatch(slice.actions.hasError(response.data));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
        }
    };
}

export function getConsentRequests(practiceId, params = {}) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const searchParams = new URLSearchParams();
            searchParams.set('ordering', '-created_at');
            if (params.status) searchParams.set('status', params.status);
            if (params.form) searchParams.set('form', params.form);
            if (params.lead) searchParams.set('lead', params.lead);
            if (params.offset !== undefined) searchParams.set('offset', params.offset);
            if (params.limit !== undefined) searchParams.set('limit', params.limit);
            if (params.search) searchParams.set('search', params.search);
            // Soft-archive filter (2026-05-28). The BE defaults to
            // ``archived=false`` (hide archived rows) when this param is
            // absent, matching the pre-archive list shape. Pass
            // ``'true'`` for the Archived view, ``'all'`` for combined.
            if (params.archived !== undefined) searchParams.set('archived', params.archived);
            const response = await callApi(
                `/api/v1/practices/${practiceId}/consent-requests/?${searchParams.toString()}`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setConsentRequests(response.data));
            } else {
                dispatch(slice.actions.hasError(response.data));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
        }
    };
}

export function revokeConsentRequest(practiceId, requestId, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/consent-requests/${requestId}/revoke/`,
                'post'
            );
            if (response.status === 200 || response.status === 204) {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: true, data: response.data });
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            if (callback) callback({ success: false });
        }
    };
}

// Soft-archive a consent request (2026-05-28). Distinct from
// ``revokeConsentRequest`` — archive hides the row from the default
// Submissions list without changing ``status`` or invalidating the
// access_token. Reversible via ``unarchiveConsentRequest``.
export function archiveConsentRequest(practiceId, requestId, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/consent-requests/${requestId}/archive/`,
                'post'
            );
            if (response.status === 200 || response.status === 204) {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: true, data: response.data });
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            if (callback) callback({ success: false });
        }
    };
}

export function unarchiveConsentRequest(practiceId, requestId, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/consent-requests/${requestId}/unarchive/`,
                'post'
            );
            if (response.status === 200 || response.status === 204) {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: true, data: response.data });
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            if (callback) callback({ success: false });
        }
    };
}

export function getLeadConsentRequests(practiceId, leadId) {
    return async () => {
        try {
            dispatch(slice.actions.startLeadConsentLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/consent-requests/?ordering=-created_at`
            );
            if (response.status === 200) {
                const { data } = response;
                dispatch(slice.actions.setLeadConsentRequests({
                    results: data.results || data || [],
                    count: data.count ?? (data.results || data || []).length,
                }));
            } else {
                dispatch(slice.actions.setLeadConsentRequests({ results: [], count: 0 }));
            }
        } catch (error) {
            dispatch(slice.actions.setLeadConsentRequests({ results: [], count: 0 }));
        }
    };
}

/**
 * Fetch custom form type configurations from the practice choices API.
 * Custom form types are stored as choices with metadata.type = 'form_type_config'.
 * Falls back to an empty array on error — callers always merge with SYSTEM_FORM_TYPES.
 */
export function getFormTypes(practiceId) {
    return async () => {
        try {
            dispatch(slice.actions.startFormTypesLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/choices/?metadata__type=form_type_config`
            );
            if (response.status === 200) {
                const results = response.data?.results || response.data || [];
                dispatch(slice.actions.setFormTypes(results));
            } else {
                dispatch(slice.actions.setFormTypes([]));
            }
        } catch {
            dispatch(slice.actions.stopFormTypesLoading());
        }
    };
}

/**
 * Create a custom form type choice.
 * @param {string} practiceId
 * @param {{ value: string, label: string, color: string, icon: string }} typeData
 * @param {Function} callback - called with { success, data }
 */
export function saveCustomFormType(practiceId, typeData, callback) {
    return async () => {
        try {
            const payload = {
                name: typeData.label,
                value: typeData.value,
                metadata: {
                    type: 'form_type_config',
                    color: typeData.color || '#637381',
                    icon: typeData.icon || 'solar:document-bold-duotone',
                },
            };
            const response = await callApi(
                `/api/v1/practices/${practiceId}/choices/`,
                'post',
                payload
            );
            if (response.status === 201) {
                if (callback) callback({ success: true, data: response.data });
                dispatch(getFormTypes(practiceId));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to save form type' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to save form type' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Delete a custom form type choice.
 * @param {string} practiceId
 * @param {string|number} choiceId  - the choice record's backend ID
 * @param {Function} callback
 */
export function deleteCustomFormType(practiceId, choiceId, callback) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/choices/${choiceId}/`,
                'delete'
            );
            if (response.status === 204) {
                if (callback) callback({ success: true });
                dispatch(getFormTypes(practiceId));
            } else {
                dispatch(updateError({ success: false, message: 'Failed to delete form type' }));
                if (callback) callback({ success: false });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to delete form type' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Fetch all form submissions for a specific lead.
 * Endpoint: GET /api/v1/practices/{practiceId}/leads/{leadId}/form-submissions/
 */
export function getLeadFormSubmissions(practiceId, leadId) {
    return async () => {
        try {
            dispatch(slice.actions.startLeadSubmissionsLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/form-submissions/?ordering=-submitted_at`
            );
            if (response.status === 200) {
                const { data } = response;
                dispatch(slice.actions.setLeadSubmissions({
                    results: data.results || data || [],
                    count: data.count ?? (data.results || data || []).length,
                }));
            } else {
                dispatch(slice.actions.setLeadSubmissions({ results: [], count: 0 }));
            }
        } catch (error) {
            dispatch(slice.actions.setLeadSubmissions({ results: [], count: 0 }));
        }
    };
}

/**
 * Resend a consent request that has expired.
 * Endpoint: POST /api/v1/practices/{practiceId}/consent-requests/{requestId}/resend/
 */
export function resendConsentRequest(practiceId, requestId, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/consent-requests/${requestId}/resend/`,
                'post'
            );
            if (response.status === 200 || response.status === 201) {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: true, data: response.data });
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            if (callback) callback({ success: false });
        }
    };
}

// ─── Form Trigger Thunks ─────────────────────────────────────────────────────
// Auto-send rules are stored as practice choices with metadata.type = 'form_trigger_config'.

/**
 * Fetch all auto-send trigger rules for a practice.
 */
export function getFormTriggers(practiceId) {
    return async () => {
        try {
            dispatch(slice.actions.startFormTriggersLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/choices/?metadata__type=form_trigger_config&ordering=created_at`
            );
            if (response.status === 200) {
                const results = response.data?.results || response.data || [];
                dispatch(slice.actions.setFormTriggers(results));
            } else {
                dispatch(slice.actions.setFormTriggers([]));
            }
        } catch {
            dispatch(slice.actions.setFormTriggers([]));
        }
    };
}

/**
 * Create a new auto-send trigger rule.
 * @param {string} practiceId
 * @param {{ trigger_status: string, form_id: number, form_name: string, form_type: string }} triggerData
 * @param {Function} callback
 */
export function saveFormTrigger(practiceId, triggerData, callback) {
    return async () => {
        try {
            const payload = {
                name: `form_trigger_${triggerData.trigger_status}`,
                value: triggerData.trigger_status,
                metadata: {
                    type: 'form_trigger_config',
                    trigger_status: triggerData.trigger_status,
                    form_id: triggerData.form_id,
                    form_name: triggerData.form_name,
                    form_type: triggerData.form_type || 'CONSENT',
                },
            };
            const response = await callApi(
                `/api/v1/practices/${practiceId}/choices/`,
                'post',
                payload
            );
            if (response.status === 201) {
                if (callback) callback({ success: true, data: response.data });
                dispatch(getFormTriggers(practiceId));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to save trigger rule' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to save trigger rule' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Delete an auto-send trigger rule by choice ID.
 */
export function deleteFormTrigger(practiceId, choiceId, callback) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/choices/${choiceId}/`,
                'delete'
            );
            if (response.status === 204) {
                if (callback) callback({ success: true });
                dispatch(getFormTriggers(practiceId));
            } else {
                dispatch(updateError({ success: false, message: 'Failed to delete trigger rule' }));
                if (callback) callback({ success: false });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to delete trigger rule' }));
            if (callback) callback({ success: false });
        }
    };
}

// ─── Form Branding Thunks ────────────────────────────────────────────────────
// Branding config is stored as a single practice choice with metadata.type = 'form_branding_config'.

/**
 * Fetch the practice form branding configuration.
 * Returns the first result (there should only be one).
 */
export function getFormBranding(practiceId) {
    return async () => {
        try {
            dispatch(slice.actions.startFormBrandingLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/choices/?metadata__type=form_branding_config&ordering=-created_at&limit=1`
            );
            if (response.status === 200) {
                const results = response.data?.results || response.data || [];
                dispatch(slice.actions.setFormBranding(results[0] || null));
            } else {
                dispatch(slice.actions.setFormBranding(null));
            }
        } catch {
            dispatch(slice.actions.setFormBranding(null));
        }
    };
}

/**
 * Create or update the practice form branding configuration.
 * If existingId is provided, PATCHes the existing choice; otherwise POSTs a new one.
 * @param {string} practiceId
 * @param {{ logo_url: string, display_name: string, primary_color: string, contact_email: string, contact_phone: string }} data
 * @param {string|number|null} existingId  - the choice record ID to update, or null to create
 * @param {Function} callback
 */
export function saveFormBranding(practiceId, data, existingId, callback) {
    return async () => {
        try {
            const payload = {
                name: 'form_branding',
                value: 'form_branding',
                metadata: {
                    type: 'form_branding_config',
                    logo_url: data.logo_url || '',
                    display_name: data.display_name || '',
                    primary_color: data.primary_color || '#00AB55',
                    contact_email: data.contact_email || '',
                    contact_phone: data.contact_phone || '',
                },
            };
            const url = existingId
                ? `/api/v1/practices/${practiceId}/choices/${existingId}/`
                : `/api/v1/practices/${practiceId}/choices/`;
            const method = existingId ? 'patch' : 'post';
            const response = await callApi(url, method, payload);
            const expectedStatus = existingId ? 200 : 201;
            if (response.status === expectedStatus) {
                if (callback) callback({ success: true, data: response.data });
                dispatch(getFormBranding(practiceId));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to save branding' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to save branding' }));
            if (callback) callback({ success: false });
        }
    };
}

// ─── Treatment Plan Template Thunks ─────────────────────────────────────────

/**
 * Fetch all treatment plan templates for a practice.
 * Endpoint: GET /api/v1/practices/{practiceId}/treatment-plan-templates/
 */
export function getTreatmentPlanTemplates(practiceId, params = {}) {
    return async () => {
        try {
            dispatch(slice.actions.startTreatmentPlanTemplatesLoading());
            const searchParams = new URLSearchParams();
            searchParams.set('ordering', '-created_at');
            if (params.offset !== undefined) searchParams.set('offset', params.offset);
            if (params.limit !== undefined) searchParams.set('limit', params.limit);
            if (params.search) searchParams.set('search', params.search);
            const response = await callApi(
                `/api/v1/practices/${practiceId}/treatment-plan-templates/?${searchParams.toString()}`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setTreatmentPlanTemplates(response.data));
            } else {
                dispatch(slice.actions.stopTreatmentPlanTemplatesLoading());
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to load templates' }));
            }
        } catch (error) {
            dispatch(slice.actions.stopTreatmentPlanTemplatesLoading());
            dispatch(updateError({ success: false, message: error.message || 'Failed to load templates' }));
        }
    };
}

/**
 * Create a new treatment plan template.
 * Endpoint: POST /api/v1/practices/{practiceId}/treatment-plan-templates/
 */
export function createTreatmentPlanTemplate(practiceId, data, callback) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/treatment-plan-templates/`,
                'post',
                data
            );
            if (response.status === 201) {
                if (callback) callback({ success: true, data: response.data });
                dispatch(getTreatmentPlanTemplates(practiceId));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to create template' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to create template' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Update an existing treatment plan template.
 * Endpoint: PATCH /api/v1/practices/{practiceId}/treatment-plan-templates/{templateId}/
 */
export function updateTreatmentPlanTemplate(practiceId, templateId, data, callback) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/treatment-plan-templates/${templateId}/`,
                'patch',
                data
            );
            if (response.status === 200) {
                if (callback) callback({ success: true, data: response.data });
                dispatch(getTreatmentPlanTemplates(practiceId));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to update template' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to update template' }));
            if (callback) callback({ success: false });
        }
    };
}

// ─── Per-Lead Treatment Plan Thunks ──────────────────────────────────────────
// Endpoint: /api/v1/practices/{practiceId}/leads/{leadId}/treatment-plans/
// (Placeholder endpoints — backend implementation tracked in PROMPT_06)

/**
 * Fetch all treatment plans for a specific lead.
 */
export function getLeadTreatmentPlans(practiceId, leadId) {
    return async () => {
        try {
            dispatch(slice.actions.startLeadTreatmentPlansLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/treatment-plans/?ordering=-created_at`
            );
            if (response.status === 200) {
                const { data } = response;
                dispatch(slice.actions.setLeadTreatmentPlans({
                    results: data.results || data || [],
                    count: data.count ?? (data.results || data || []).length,
                }));
            } else {
                dispatch(slice.actions.setLeadTreatmentPlans({ results: [], count: 0 }));
            }
        } catch {
            dispatch(slice.actions.setLeadTreatmentPlans({ results: [], count: 0 }));
        }
    };
}

/**
 * Create a new treatment plan for a lead.
 * Stores practice-filled fields, options, cost items, and patient info.
 * @param {string} practiceId
 * @param {string|number} leadId
 * @param {object} data - { options: [], patient_name, treatment_name, form_id, expires_in_hours }
 * @param {Function} callback - called with { success, data: { access_token, url, ... } }
 */
export function createLeadTreatmentPlan(practiceId, leadId, data, callback) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/treatment-plans/`,
                'post',
                data
            );
            if (response.status === 201 || response.status === 200) {
                if (callback) callback({ success: true, data: response.data });
                dispatch(getLeadTreatmentPlans(practiceId, leadId));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to create treatment plan' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to create treatment plan' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Update an existing treatment plan (e.g., after editing).
 */
export function updateLeadTreatmentPlan(practiceId, leadId, planId, data, callback) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/treatment-plans/${planId}/`,
                'patch',
                data
            );
            if (response.status === 200) {
                if (callback) callback({ success: true, data: response.data });
                dispatch(getLeadTreatmentPlans(practiceId, leadId));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to update treatment plan' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to update treatment plan' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Send an existing treatment plan via a channel (EMAIL / WHATSAPP / SMS).
 */
export function sendLeadTreatmentPlan(practiceId, leadId, planId, channel, callback) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/treatment-plans/${planId}/send/`,
                'post',
                { channel }
            );
            if (response.status === 200 || response.status === 201) {
                if (callback) callback({ success: true, data: response.data });
                dispatch(getLeadTreatmentPlans(practiceId, leadId));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to send treatment plan' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to send treatment plan' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Delete a treatment plan template.
 * Endpoint: DELETE /api/v1/practices/{practiceId}/treatment-plan-templates/{templateId}/
 */
export function deleteTreatmentPlanTemplate(practiceId, templateId, callback) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/treatment-plan-templates/${templateId}/`,
                'delete'
            );
            if (response.status === 204) {
                if (callback) callback({ success: true });
                dispatch(getTreatmentPlanTemplates(practiceId));
            } else {
                dispatch(updateError({ success: false, message: 'Failed to delete template' }));
                if (callback) callback({ success: false });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to delete template' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Create a consent request using the new API fields.
 * Supports: treatment_ids, sequence_id, sequence_order, channel.
 * Endpoint: POST /api/v1/practices/{practiceId}/consent-requests/
 */
export function createConsentRequest(practiceId, data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/consent-requests/`,
                'post',
                data
            );
            if (response.status === 200 || response.status === 201) {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: true, data: response.data });
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to create consent request' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Failed to create consent request' }));
            if (callback) callback({ success: false });
        }
    };
}
